<template>
  <div>
    <div class="choice-cards">
      <study-visit-exchange-choice-card
        v-model="applicationData.choice"
        :title="$t('study_visit_application.selected_choice')"
        @show-select-exchange="showSelectExchangeModal"
      />
    </div>

    <teleport
      append
      to="#vue-portal-target"
    >
      <remote-modal
        ref="selectExchangeModal"
        :allow-keyboard="true"
        :loading-indicator-in-footer="false"
        :loading-message="$t('application_form.select_exchange.loading')"
        :modal-title="$t('application_form.select_exchange.title')"
        modal-class="modal-tabular"
        modal-dialog-class="modal-xl"
        @details-loaded="onSelectExchangesLoaded"
      >
        <template
          #modal-content="{ modalState }"
        >
          <div
            v-if="selectExchangesList.length > 0"
            class="table-wrapper"
          >
            <div class="table-responsive">
              <table class="table table-hover table-sm study-visit-periods">
                <thead>
                <tr>
                  <th>{{ $t('study_visit_application.choice.institution_name_period') }}</th>
                  <th>{{ $t('study_visit_application.choice.language') }}</th>
                  <th>{{ $t('study_visit_application.choice.available_places') }}</th>
                  <th class="tight"/>
                </tr>
                </thead>
                <tbody>
                <template
                  v-for="exchange of selectExchangesList"
                  :key="`sv-${exchange.id}`"
                >
                  <tr class="table-secondary">
                    <td colspan="3"><strong>{{ exchange.institutionName }}</strong></td>
                    <td class="tight-right">
                      <a
                        v-if="exchangeRequirementsVisible(exchange)"
                        href="#"
                        @click.prevent="showExchangeRequirements(exchange)"
                      >
                        View requirements
                      </a>
                    </td>
                  </tr>
                  <tr
                    v-for="period of exchange.periods"
                    :key="`svp-${period.id}`"
                    class="period"
                  >
                    <td class="dates">{{ period.dates }}</td>
                    <td>{{ period.language }}</td>
                    <td class="text-center">{{ period.availablePlaces }}</td>
                    <td class="tight-right">
                      <button
                        class="btn btn-sm btn-info"
                        type="button"
                        @click.prevent="selectExchange(period)"
                      >
                        {{ $t('long_term_application.choice.select') }}
                      </button>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>
          </div>
          <div
            v-else-if="!modalState.isLoading"
            class="alert alert-warning"
          >
            {{ $t('application_form.select_exchange.no_exchanges_found') }}
          </div>
        </template>
        <template v-slot:modal-footer>
          <button
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
            type="button"
          >
            {{ $t('modal.button.close') }}
          </button>
        </template>
      </remote-modal>
      <remote-modal
        ref="exchangeRequirementsModal"
        :allow-keyboard="true"
        :modal-title="$t('application_form.exchange_requirements_modal.title')"
        modal-dialog-class="modal-xl exchange-requirements"
        @details-loaded="onExchangeRequirementsLoaded"
      >
        <template v-slot:modal-content>
          <div
            v-if="exchangeRequirementsLoaded"
            class="requirement-list"
          >
            <div
              class="requirement"
              v-html="exchangeRequirements"
            />
          </div>
        </template>
      </remote-modal>
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, reactive, ref, useTemplateRef, watch} from 'vue'
import {useTranslation} from 'i18next-vue'
import Jsona from 'jsona'

import RemoteModal from '../../vue-components/remote-modal.vue'
import StudyVisitExchangeChoiceCard from './study-visit-exchange-choice-card.vue'

import type {StudyVisitPlace, StudyVisitPlacePeriodChoice} from '../types'
import {isNonEmptyString} from '@/js/js-utils'

const {t: $t} = useTranslation()

interface StudyVisitApplication {
  choice: StudyVisitPlacePeriodChoice | null;
}

const props = defineProps({
  availableExchangesUrl: {type: String},
})

const selectExchangesList = ref<StudyVisitPlace[]>([])
const applicationData = reactive<StudyVisitApplication>({
  choice: null,
})
const applicationDataEl = ref<HTMLInputElement>()
const exchangeRequirements = ref<string>()

const selectExchangeModal = useTemplateRef<InstanceType<typeof RemoteModal>>('selectExchangeModal')
const exchangeRequirementsModal = useTemplateRef<InstanceType<typeof RemoteModal>>('exchangeRequirementsModal')

const exchangeRequirementsLoaded = computed((): boolean => {
  return isNonEmptyString(exchangeRequirements.value)
})

watch(applicationData, () => {
  storeApplicationData()
}, {deep: true})

onMounted(() => {
  const dataJsonEl = document.querySelector('input[ref="applicationDataJson"]')
  if (dataJsonEl instanceof HTMLInputElement) {
    applicationDataEl.value = dataJsonEl
    try {
      const appData = JSON.parse(applicationDataEl.value.value)
      Object.assign(applicationData, appData)
    } catch { /* empty */
    }
  }
})

const showSelectExchangeModal = (): void => {
  selectExchangesList.value = []
  if (isNonEmptyString(props.availableExchangesUrl)) {
    selectExchangeModal.value?.showModal(props.availableExchangesUrl)
  }
}

const onSelectExchangesLoaded = (data: any): void => {
  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    selectExchangesList.value = loadedData as StudyVisitPlace[]
  }
}

const prepareExchange = (exchange: StudyVisitPlacePeriodChoice, previousExchange: StudyVisitPlacePeriodChoice | null): StudyVisitPlacePeriodChoice => {
  // copy exchange
  exchange = JSON.parse(JSON.stringify(exchange))

  if (previousExchange) {
    exchange.specialInterest = previousExchange.specialInterest
  }
  return exchange
}

const selectExchange = (exchange: StudyVisitPlacePeriodChoice | null): void => {
  selectExchangeModal.value?.hideModal()

  if (exchange) {
    applicationData.choice = prepareExchange(exchange, applicationData.choice)
  }
}

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(applicationData)
  }
}

const exchangeRequirementsVisible = (exchange: StudyVisitPlace): boolean => {
  return isNonEmptyString(exchange.links?.requirements)
}

const showExchangeRequirements = (exchange: StudyVisitPlace): void => {
  exchangeRequirements.value = undefined
  if (isNonEmptyString(exchange.links?.requirements)) {
    exchangeRequirementsModal.value?.showModal(exchange.links.requirements)
  }
}

const onExchangeRequirementsLoaded = (data: any): void => {
  if (isNonEmptyString(data)) {
    exchangeRequirements.value = data
  }
}
</script>
